import { useState, useEffect } from 'react';
import './Header.css';

type PageKey =
  | 'home'
  | 'about'
  | 'contact'
  | 'consultants'
  | 'companies'
  | 'popi-policy'
  | 'privacy-policy'
  | 'terms-of-service'
  | 'terms-consultants'
  | 'terms-companies'
  | 'community-guidelines'
  | 'medical-disclaimer'
  | 'download-documents'
  | 'ethics-committees'
  | 'rfp-submission'
  | 'analytics'
  | 'subscriptions'
  | 'profile'
  | 'login'
  | 'marketplace'
  | 'chat'
  | 'connections'
  | 'notifications'
  | 'register'
  | 'consultant-register'
  | 'forgot-password'
  | 'community'
  | 'sites'
  | 'post-job';
interface HeaderProps {
  currentPage: PageKey;
  setCurrentPage: (page: PageKey) => void;
  isLoggedIn: boolean;
  currentUser?: { id: string; role: string } | null;
  onLogout: () => void;
}

export default function Header({
  currentPage,
  setCurrentPage,
  isLoggedIn,
  currentUser,
  onLogout,
}: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [tagline, setTagline] = useState('Where Clinical Minds Connect');

  useEffect(() => {
    fetch('/api/content')
      .then((res) => res.json())
      .then((data) => {
        if (data.tagline) {
          setTagline(data.tagline);
        }
      })
      .catch(() => {
        // Use default tagline on error
      });
  }, []);

  const handleNavClick = (page: PageKey) => {
    // Check if page requires login
    const protectedPages: PageKey[] = [
      'consultants',
      'companies',
      'marketplace',
      'sites',
      'community',
      'profile',
      'chat',
      'connections',
      'notifications',
    ];

    if (!isLoggedIn && protectedPages.includes(page)) {
      if (
        confirm(
          'You need to be logged in to access this page. Would you like to login or register?'
        )
      ) {
        setCurrentPage('login');
        setIsMenuOpen(false);
      }
      return;
    }

    // Navigate - scrolling is handled by App.tsx
    setCurrentPage(page);
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    onLogout();
    setIsMenuOpen(false);
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-top">
          <div className="logo">
            <img
              src="/a professional logo .webp"
              alt="CTE Logo"
              className="logo-image"
              width="80"
              height="80"
              fetchPriority="high"
            />
            <div className="logo-text">
              <h1>Clinical Trial Expertise</h1>
              <p className="tagline">{tagline}</p>
            </div>
          </div>
          <button
            className="hamburger-menu"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={isMenuOpen ? 'open' : ''}></span>
            <span className={isMenuOpen ? 'open' : ''}></span>
            <span className={isMenuOpen ? 'open' : ''}></span>
          </button>
        </div>
        <nav className={`nav ${isMenuOpen ? 'open' : ''}`}>
          <button
            className={`nav-link ${currentPage === 'home' ? 'active' : ''}`}
            onClick={() => handleNavClick('home')}
          >
            Home
          </button>
          <button
            className={`nav-link ${currentPage === 'about' ? 'active' : ''}`}
            onClick={() => handleNavClick('about')}
          >
            About
          </button>

          <button
            className={`nav-link ${currentPage === 'consultants' ? 'active' : ''}`}
            onClick={() => handleNavClick('consultants')}
          >
            Consultants
          </button>
          <button
            className={`nav-link ${currentPage === 'companies' ? 'active' : ''}`}
            onClick={() => handleNavClick('companies')}
          >
            Companies
          </button>
          <button
            className={`nav-link ${currentPage === 'marketplace' ? 'active' : ''}`}
            onClick={() => handleNavClick('marketplace')}
          >
            Jobs
          </button>
          <button
            className={`nav-link ${currentPage === 'sites' ? 'active' : ''}`}
            onClick={() => handleNavClick('sites')}
          >
            Sites
          </button>
          <button
            className={`nav-link ${currentPage === 'community' ? 'active' : ''}`}
            onClick={() => handleNavClick('community')}
          >
            Community
          </button>
          {isLoggedIn && (
            <button
              className={`nav-link ${currentPage === 'connections' ? 'active' : ''}`}
              onClick={() => handleNavClick('connections')}
            >
              🔗 Connections
            </button>
          )}
          {isLoggedIn && (
            <button
              className={`nav-link ${currentPage === 'chat' ? 'active' : ''}`}
              onClick={() => handleNavClick('chat')}
            >
              💬 Chat
            </button>
          )}
          {isLoggedIn && (
            <button
              className={`nav-link ${currentPage === 'notifications' ? 'active' : ''}`}
              onClick={() => handleNavClick('notifications')}
            >
              🔔 Notifications
            </button>
          )}

          <button
            className={`nav-link ${currentPage === 'contact' ? 'active' : ''}`}
            onClick={() => handleNavClick('contact')}
          >
            Contact
          </button>

          {isLoggedIn ? (
            <div className="auth-section">
              <button
                className={`nav-link ${currentPage === 'profile' ? 'active' : ''}`}
                onClick={() => handleNavClick('profile')}
              >
                {currentUser?.role === 'professional' && '👨‍⚕️'}
                {currentUser?.role === 'company' && '🏢'}
                {currentUser?.role === 'team' && '👥'}
                My Profile
              </button>
              <button className="nav-link logout-btn" onClick={handleLogout}>
                Logout
              </button>
            </div>
          ) : (
            <button
              className={`nav-link login-btn ${currentPage === 'login' ? 'active' : ''}`}
              onClick={() => handleNavClick('login')}
            >
              Login
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}
